'use client'

import { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { useApp } from '@/context/AppContext'
import { api } from '@/lib/api'
import { ArrowLeft, CheckCircle, X } from 'lucide-react'
import Link from 'next/link'

type Step = 'barcode' | 'info' | 'prix' | 'stock' | 'done'

// ─── OCR Camera Scanner ───────────────────────────────────────────────────────
function OcrCamera({ onDetected, onClose }: { onDetected: (val: string) => void; onClose: () => void; }) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [busy, setBusy] = useState(false)
  const [tokens, setTokens] = useState<string[]>([])
  const [msg, setMsg] = useState('Cadre la référence dans le rectangle')

  useEffect(() => {
    startCamera()
    return () => { streamRef.current?.getTracks().forEach(t => t.stop()) }
  }, [])

  async function startCamera() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }
    } catch {
      setMsg('Accès caméra refusé — utilise la saisie manuelle')
    }
  }

  async function capture() {
    const video = videoRef.current
    if (!video || busy || video.videoWidth === 0) return
    setBusy(true)
    setTokens([])
    setMsg('Analyse...')
    try {
      // Crop uniquement la bande centrale du rectangle (40% central en hauteur)
      const vw = video.videoWidth
      const vh = video.videoHeight
      const bandH = Math.round(vh * 0.38)
      const bandY = Math.round((vh - bandH) / 2)

      const canvas = document.createElement('canvas')
      // Upscale x2 pour meilleure OCR
      canvas.width = vw * 2
      canvas.height = bandH * 2
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(video, 0, bandY, vw, bandH, 0, 0, vw * 2, bandH * 2)

      const dataUrl = canvas.toDataURL('image/png')
      const Tesseract = (await import('tesseract.js')).default
      const { data } = await Tesseract.recognize(dataUrl, 'eng', {
        logger: () => {}
      })
      const text = data.text || ''
      // Extraire tokens alphanumériques >= 3 chars
      const raw = text.toUpperCase().match(/[A-Z0-9][A-Z0-9\-\/]{2,}/g) || []
      const uniq = Array.from(new Set(
        raw.map(t => t.replace(/[^A-Z0-9\-\/]/g, '')).filter(t => t.length >= 3)
      ))
      // Priorité: mélange lettres+chiffres (ex: CRDN570ZX)
      uniq.sort((a, b) => {
        const score = (s: string) => (/[A-Z]/.test(s) && /[0-9]/.test(s) ? 3 : 1) * s.length
        return score(b) - score(a)
      })
      const found = uniq.slice(0, 10)
      if (found.length > 0) {
        setTokens(found)
        setMsg('Touche la bonne référence :')
      } else {
        setMsg('Rien trouvé — rapproche-toi et réessaie')
      }
    } catch (e: any) {
      setMsg('Erreur: ' + (e?.message || ''))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-12 pb-3 z-10">
        <button onClick={onClose} className="text-white p-2"><X className="w-6 h-6" /></button>
        <p className="text-white font-semibold text-sm">Scanner la référence</p>
        <span className="w-10" />
      </div>

      {/* Camera + viewfinder */}
      <div className="flex-1 relative overflow-hidden">
        <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" muted playsInline />
        {/* Overlay sombre autour du rectangle */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Bande du haut */}
          <div className="absolute top-0 left-0 right-0 bg-black/55" style={{ height: '31%' }} />
          {/* Bande du bas */}
          <div className="absolute bottom-0 left-0 right-0 bg-black/55" style={{ height: '31%' }} />
          {/* Rectangle central — coins colorés */}
          <div className="absolute left-6 right-6" style={{ top: '31%', height: '38%' }}>
            {/* Coins */}
            <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-indigo-400 rounded-tl-lg" />
            <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-indigo-400 rounded-tr-lg" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-indigo-400 rounded-bl-lg" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-indigo-400 rounded-br-lg" />
            {/* Ligne scan */}
            <div className="absolute left-2 right-2 top-1/2 h-0.5 bg-indigo-400/70"
              style={{ boxShadow: '0 0 8px #818cf8' }} />
          </div>
        </div>
      </div>

      {/* Bottom panel */}
      <div className="bg-gray-950 px-5 pt-4 pb-8 space-y-4">
        <p className="text-white/60 text-sm text-center min-h-[20px]">{msg}</p>

        {/* Chips résultats */}
        {tokens.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center">
            {tokens.map(t => (
              <button key={t} onClick={() => { streamRef.current?.getTracks().forEach(tr => tr.stop()); onDetected(t) }}
                className="px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-mono font-bold text-sm active:scale-95 active:bg-indigo-700">
                {t}
              </button>
            ))}
          </div>
        )}

        <button onClick={capture} disabled={busy}
          className="w-full py-4 bg-white text-gray-900 rounded-2xl font-bold text-lg active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2">
          {busy
            ? <><span className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" /> Analyse...</>
            : '📸 Capturer'}
        </button>
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function NewArticlePage() {
  const { session } = useApp()
  const router = useRouter()

  const [step, setStep] = useState<Step>('barcode')
  const [loading, setLoading] = useState(false)
  const [showOcr, setShowOcr] = useState(false)
  const [checking, setChecking] = useState(false)
  const [form, setForm] = useState({
    name: '', barcode: '', category_id: '', unit: 'pièce',
    ras_mel: '', prix_vente: '', prix_min: '', stock: '', stock_alert: '5'
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [suggestions, setSuggestions] = useState<any[]>([])
  const [searching, setSearching] = useState(false)
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!session) { router.replace('/login'); return }
  }, [session])

  function set(key: string, val: string) {
    setForm(f => ({ ...f, [key]: val }))
    setErrors(e => ({ ...e, [key]: '' }))
    if (key === 'barcode') {
      const v = val.toUpperCase()
      if (searchTimeout.current) clearTimeout(searchTimeout.current)
      if (v.length >= 3) {
        setSearching(true)
        searchTimeout.current = setTimeout(async () => {
          const res = await fetch(`/api/articles?shop_id=${session!.shop.id}&search=${encodeURIComponent(v)}`)
          const data = await res.json()
          setSuggestions(Array.isArray(data) ? data.slice(0, 6) : [])
          setSearching(false)
        }, 300)
      } else {
        setSuggestions([])
        setSearching(false)
      }
    }
  }

  async function checkBarcode(code: string) {
    const trimmed = code.trim().toUpperCase()
    if (!trimmed) { setErrors({ barcode: 'Référence obligatoire' }); return }
    setChecking(true)
    setSuggestions([])
    const res = await fetch(`/api/articles?shop_id=${session!.shop.id}&search=${encodeURIComponent(trimmed)}`)
    const articles = await res.json()
    setChecking(false)
    const existing = Array.isArray(articles)
      ? articles.find((a: any) => a.barcode?.toUpperCase() === trimmed)
      : null
    if (existing) {
      router.push(`/articles/${existing.id}`)
    } else {
      setForm(f => ({ ...f, barcode: trimmed }))
      setStep('info')
    }
  }

  function validate() {
    const e: Record<string, string> = {}
    if (step === 'info' && !form.name.trim()) e.name = 'Nom obligatoire'
    if (step === 'prix') {
      if (!form.ras_mel) e.ras_mel = 'Obligatoire'
      if (!form.prix_vente) e.prix_vente = 'Obligatoire'
      if (!form.prix_min) e.prix_min = 'Obligatoire'
      if (form.prix_min && form.prix_vente && parseFloat(form.prix_min) > parseFloat(form.prix_vente))
        e.prix_min = 'Prix min > prix vente!'
    }
    if (step === 'stock' && !form.stock) e.stock = 'Obligatoire'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function next() {
    if (!validate()) return
    const steps: Step[] = ['info', 'prix', 'stock', 'done']
    const idx = steps.indexOf(step)
    if (steps[idx + 1] === 'done') {
      setLoading(true)
      await api.createArticle({
        shop_id: session!.shop.id,
        name: form.name.trim(),
        barcode: form.barcode || null,
        category_id: form.category_id || null,
        unit: form.unit,
        ras_mel: parseFloat(form.ras_mel),
        prix_vente: parseFloat(form.prix_vente),
        prix_min: parseFloat(form.prix_min),
        stock: parseInt(form.stock),
        stock_alert: parseInt(form.stock_alert)
      })
      setLoading(false)
    }
    setStep(steps[idx + 1])
  }

  const stepNum: Record<Step, number> = { barcode: 0, info: 1, prix: 2, stock: 3, done: 4 }
  const stepLabels = [{ s: 'info', label: '1. Article' }, { s: 'prix', label: '2. Prix' }, { s: 'stock', label: '3. Stock' }]

  return (
    <div className="min-h-screen pb-6">
      {showOcr && (
        <OcrCamera
          onDetected={code => { setShowOcr(false); checkBarcode(code) }}
          onClose={() => setShowOcr(false)}
        />
      )}

      <div className="bg-white sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-3 px-4 pt-12 pb-4">
          <Link href="/articles" className="p-2 -ml-2 text-gray-600"><ArrowLeft className="w-6 h-6" /></Link>
          <h1 className="text-xl font-bold text-gray-900">Nouvel article</h1>
        </div>
        {step !== 'barcode' && step !== 'done' && (
          <div className="flex px-4 pb-4 gap-2">
            {stepLabels.map(({ s, label }, i) => (
              <div key={s} className="flex-1 flex flex-col items-center gap-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                  ${s === step ? 'bg-indigo-600 text-white' : stepNum[s as Step] < stepNum[step] ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                  {i + 1}
                </div>
                <span className="text-xs text-gray-500">{label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 pt-6">

        {/* ── STEP 0: BARCODE / REF ── */}
        {step === 'barcode' && (
          <div className="space-y-4">
            {/* Option 1: Scanner avec caméra */}
            <button onClick={() => setShowOcr(true)}
              className="w-full py-12 rounded-3xl border-2 border-dashed border-indigo-300 bg-indigo-50 flex flex-col items-center gap-3 active:scale-95 transition-transform">
              <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center text-3xl">
                📷
              </div>
              <span className="text-indigo-700 font-bold text-lg">Scanner la référence</span>
              <span className="text-indigo-400 text-sm">Pointe la caméra vers le texte (CRDN570ZX…)</span>
            </button>

            {/* Séparateur */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-gray-400 text-sm font-medium">ou</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Option 2: Saisie manuelle */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Saisir manuellement</label>
              <div className="relative">
                <input
                  value={form.barcode}
                  onChange={e => set('barcode', e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && checkBarcode(form.barcode)}
                  placeholder="Ex: CRDN570ZX ou 619123456789"
                  autoCapitalize="characters"
                  className={`w-full px-4 py-4 rounded-2xl border bg-gray-50 outline-none focus:bg-white focus:border-indigo-500 text-lg font-mono uppercase ${errors.barcode ? 'border-red-400' : 'border-gray-200'}`}
                />
                {searching && (
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                )}
              </div>
              {errors.barcode && <p className="text-red-500 text-sm mt-1">{errors.barcode}</p>}

              {/* Suggestions */}
              {suggestions.length > 0 && (
                <div className="mt-2 border border-gray-200 rounded-2xl overflow-hidden shadow-lg bg-white">
                  {suggestions.map((a, i) => (
                    <button key={a.id} onClick={() => router.push(`/articles/${a.id}`)}
                      className={`w-full px-4 py-3 flex items-center justify-between text-left active:bg-indigo-50 ${i < suggestions.length - 1 ? 'border-b border-gray-100' : ''}`}>
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">{a.name}</p>
                        <p className="text-xs font-mono text-indigo-500">{a.barcode}</p>
                      </div>
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-lg">{a.stock} {a.unit}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => checkBarcode(form.barcode)}
              disabled={checking || !form.barcode.trim()}
              className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg disabled:opacity-50 flex items-center justify-center gap-2 active:scale-95">
              {checking
                ? <span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : 'Vérifier →'}
            </button>
          </div>
        )}

        {/* ── STEP 1: INFO ── */}
        {step === 'info' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-2xl px-4 py-2.5">
              <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
              <span className="text-green-700 text-sm font-mono font-semibold flex-1">{form.barcode}</span>
              <button onClick={() => setStep('barcode')} className="text-gray-400 text-xs underline">Changer</button>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Nom *</label>
              <input autoFocus value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="Ex: Réfrigérateur Condor 570L"
                className={`w-full px-4 py-3.5 rounded-2xl border bg-gray-50 outline-none focus:bg-white focus:border-indigo-500 ${errors.name ? 'border-red-400' : 'border-gray-200'}`} />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Unité</label>
              <div className="flex gap-2">
                {['pièce', 'kg', 'litre', 'boîte'].map(u => (
                  <button key={u} onClick={() => set('unit', u)}
                    className={`flex-1 py-2.5 rounded-2xl text-sm font-semibold border-2 ${form.unit === u ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600'}`}>
                    {u}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 2: PRIX ── */}
        {step === 'prix' && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3">
              <p className="text-amber-700 text-sm">💡 <strong>Ras mel</strong> = combien 5sertou. <strong>Prix min</strong> = moins que ça = alert!</p>
            </div>
            {[
              { key: 'ras_mel', label: 'Ras mel (Prix achat) *' },
              { key: 'prix_vente', label: 'Prix vente normal *' },
              { key: 'prix_min', label: 'Prix minimum (alerte) *' },
            ].map(({ key, label }) => (
              <div key={key}>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">{label}</label>
                <div className="relative">
                  <input type="number" inputMode="decimal" value={(form as any)[key]}
                    onChange={e => set(key, e.target.value)} placeholder="0.000"
                    className={`w-full px-4 py-3.5 pr-12 rounded-2xl border bg-gray-50 outline-none focus:bg-white focus:border-indigo-500 ${(errors as any)[key] ? 'border-red-400' : 'border-gray-200'}`} />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">DT</span>
                </div>
                {(errors as any)[key] && <p className="text-red-500 text-sm mt-1">{(errors as any)[key]}</p>}
              </div>
            ))}
            {form.ras_mel && form.prix_vente && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-3">
                <p className="text-green-700 text-sm">✅ Marge: <strong>+{(((parseFloat(form.prix_vente) - parseFloat(form.ras_mel)) / parseFloat(form.ras_mel)) * 100).toFixed(1)}%</strong></p>
              </div>
            )}
          </div>
        )}

        {/* ── STEP 3: STOCK ── */}
        {step === 'stock' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Stock actuel *</label>
              <input type="number" inputMode="numeric" autoFocus value={form.stock}
                onChange={e => set('stock', e.target.value)} placeholder="0"
                className={`w-full px-4 py-3.5 rounded-2xl border bg-gray-50 outline-none focus:bg-white focus:border-indigo-500 ${errors.stock ? 'border-red-400' : 'border-gray-200'}`} />
              {errors.stock && <p className="text-red-500 text-sm mt-1">{errors.stock}</p>}
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Alert quand stock {'<'} X</label>
              <input type="number" inputMode="numeric" value={form.stock_alert}
                onChange={e => set('stock_alert', e.target.value)}
                className="w-full px-4 py-3.5 rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:bg-white focus:border-indigo-500" />
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-4 space-y-1.5 text-sm">
              <h3 className="font-bold text-indigo-800 mb-2">Récap</h3>
              {[['Référence', form.barcode], ['Article', form.name], ['Ras mel', form.ras_mel + ' DT'],
                ['Prix vente', form.prix_vente + ' DT'], ['Prix min', form.prix_min + ' DT'],
                ['Stock', form.stock + ' ' + form.unit]
              ].map(([l, v]) => (
                <div key={l} className="flex justify-between">
                  <span className="text-gray-500">{l}</span>
                  <span className="font-semibold font-mono">{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── DONE ── */}
        {step === 'done' && (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-1">Article ajouté! ✅</h2>
            <p className="text-gray-500 mb-1 font-semibold">{form.name}</p>
            <p className="text-gray-400 text-sm font-mono mb-8">{form.barcode}</p>
            <div className="flex flex-col gap-3">
              <button onClick={() => {
                setForm({ name: '', barcode: '', category_id: '', unit: 'pièce', ras_mel: '', prix_vente: '', prix_min: '', stock: '', stock_alert: '5' })
                setStep('barcode')
              }} className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg">
                + Ajouter un autre
              </button>
              <Link href="/articles" className="w-full py-4 bg-gray-100 text-gray-700 rounded-2xl font-bold text-lg text-center block">
                Voir les articles
              </Link>
            </div>
          </div>
        )}

        {(step === 'info' || step === 'prix' || step === 'stock') && (
          <button onClick={next} disabled={loading}
            className="w-full mt-8 py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
            {loading
              ? <span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : step === 'stock' ? 'Enregistrer ✓' : 'Suivant →'}
          </button>
        )}
      </div>
    </div>
  )
}
